import type { Ref } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Checkbox, Field, Select } from '@leanin/ui';
import { isIndustryCode, type IndustryCode } from '@leanin/shared';
import { useIndustryOptions } from '../members';

export type CircleIndustry =
  | { readonly industryCode: null; readonly industrySpecific: false }
  | { readonly industryCode: IndustryCode; readonly industrySpecific: boolean };

export const NO_CIRCLE_INDUSTRY: CircleIndustry = { industryCode: null, industrySpecific: false };

// The Circle's stored pair as a form value. The server never returns
// "industry only" without an industry (`circles_industry_specific_needs_code`),
// so a null industry is the no-industry state.
export function circleIndustryOf(circle: {
  readonly industryCode: IndustryCode | null;
  readonly industrySpecific: boolean;
}): CircleIndustry {
  return circle.industryCode === null
    ? NO_CIRCLE_INDUSTRY
    : { industryCode: circle.industryCode, industrySpecific: circle.industrySpecific };
}

type Props = {
  readonly value: CircleIndustry;
  readonly onChange: (next: CircleIndustry) => void;
  /** Server rejection of the industry-specific flag, shown on the checkbox. */
  readonly specificError?: string | null;
  readonly disabled?: boolean;
  /** The Industry select, for a caller that lands on it. */
  readonly industryRef?: Ref<HTMLSelectElement> | undefined;
};

// The Circle's industry and whether it is for people in that industry. A
// Circle cannot be industry-specific without an industry (the server answers
// 422 otherwise), so clearing the industry clears the flag and the checkbox
// is unavailable until an industry is picked.
export function CircleIndustryFields({
  value,
  onChange,
  specificError = null,
  disabled = false,
  industryRef,
}: Props) {
  const tc = useTranslations('Circles');
  const industryOptions = useIndustryOptions();

  return (
    <>
      <Field label={tc('industryFields.industryLabel')}>
        <Select
          ref={industryRef}
          value={value.industryCode ?? ''}
          options={[{ value: '', label: tc('industryFields.notSpecified') }, ...industryOptions]}
          onChange={(e) => {
            const next = e.target.value;
            onChange(
              isIndustryCode(next)
                ? { industryCode: next, industrySpecific: value.industrySpecific }
                : NO_CIRCLE_INDUSTRY,
            );
          }}
          disabled={disabled}
        />
      </Field>
      <Field error={specificError}>
        <Checkbox
          checked={value.industrySpecific}
          onCheckedChange={(checked) => {
            if (value.industryCode !== null) {
              onChange({ industryCode: value.industryCode, industrySpecific: checked === true });
            }
          }}
          disabled={disabled || value.industryCode === null}
          label={tc('industryFields.specificLabel')}
        />
      </Field>
    </>
  );
}
